import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { UserProfilePreview } from '@atlassian/user-profile-preview';

import type {
	luminaUserProfilePreviewRelay_reportingLines$data,
	luminaUserProfilePreviewRelay_reportingLines$key,
} from './__generated__/luminaUserProfilePreviewRelay_reportingLines.graphql';
import type {
	luminaUserProfilePreviewRelay_smarts$data,
	luminaUserProfilePreviewRelay_smarts$key,
} from './__generated__/luminaUserProfilePreviewRelay_smarts.graphql';
import type { luminaUserProfilePreviewRelay_user$key } from './__generated__/luminaUserProfilePreviewRelay_user.graphql';
import type {
	luminaUserProfilePreviewRelay_workedOn$data,
	luminaUserProfilePreviewRelay_workedOn$key,
} from './__generated__/luminaUserProfilePreviewRelay_workedOn.graphql';

/** Smarts / profile preview collaborator row — must match `UserProfilePreview` expectations. */
type Collaborator = {
	accountId: string;
	avatarUrl?: string | null;
	email?: string | null;
	jobTitle?: string | null;
	name: string;
};

type RecentWorkUrls = string[];

type HierarchyUser = {
	accountId: string;
	avatarUrl?: string | null;
	jobTitle?: string | null;
	name: string;
	numberOfDirectReports?: number;
	numberOfTotalReports?: number;
};

/** Aligned with reporting-lines `HierarchyData` (avoid importing that package for types). */
type HierarchyData = {
	managers?: HierarchyUser[];
	reports?: HierarchyUser[];
	user: HierarchyUser;
};

type UserProfilePreviewRelayProps = Omit<
	React.ComponentProps<typeof UserProfilePreview>,
	'recentWorkData'
> & {
	reportingLinesFragment?: luminaUserProfilePreviewRelay_reportingLines$key;
	smartsFragment?: luminaUserProfilePreviewRelay_smarts$key;
	userFragment?: luminaUserProfilePreviewRelay_user$key;
	workedOnFragment?: luminaUserProfilePreviewRelay_workedOn$key;
};

/**
 * Same as recent-work-client Flight Deck hydrator: `url: ['links.base', 'links.webUi']`
 * resolved with `hydratedValueToString` → `join('')` (plain concat, not `new URL`).
 */
export function confluenceLinksToAbsoluteUrl(base?: string | null, webUi?: string | null): string {
	return `${base ?? ''}${webUi ?? ''}`;
}

export function mapRelayWorkedOnConnectionToRecentWorkItems(
	data: luminaUserProfilePreviewRelay_workedOn$data,
): RecentWorkUrls {
	const result: string[] = [];

	for (const edge of data.edges) {
		const item = edge?.node;
		const object = item?.object;
		if (!object) {
			continue;
		}

		const objectData = object.data;
		if (!objectData) {
			continue;
		}

		let url = '';

		if ('links' in objectData && objectData.links) {
			url = confluenceLinksToAbsoluteUrl(objectData.links.base, objectData.links.webUi);
		} else if (objectData.__typename === 'JiraIssue') {
			url = objectData.webUrl ?? '';
		} else if (
			objectData.__typename === 'JiraPlatformComment' ||
			objectData.__typename === 'JiraServiceManagementComment'
		) {
			url = objectData.webUrl ?? objectData.issue?.webUrl ?? '';
		} else if (objectData.__typename === 'ExternalDocument') {
			url = objectData.url ?? '';
		} else if (objectData.__typename === 'BitbucketPullRequest') {
			url = objectData.href ?? '';
		} else if (objectData.__typename === 'LoomVideo') {
			url = objectData.loomUrl ?? '';
		}

		if (!url) {
			continue;
		}
		result.push(url);
	}

	return result;
}

type luminaUserProfilePreviewRelay_reportingLinesKd = Extract<
	luminaUserProfilePreviewRelay_reportingLines$data['reportingLines'],
	{ __typename: 'KnowledgeDiscoveryReportingLines' }
>;

/** `user` / `managers` / `reports` rows — same shape as `luminaUserProfilePreviewRelay_reportingLines` fragment data. */
function mapKdReportingLinesUser(
	u: luminaUserProfilePreviewRelay_reportingLinesKd['user'],
): HierarchyData['user'] | null {
	if (!u?.accountIdentifier) {
		return null;
	}
	return {
		accountId: u.accountIdentifier,
		name: u.pii?.name ?? '',
		avatarUrl: u.pii?.picture ?? null,
		jobTitle: u.pii?.extendedProfile?.jobTitle ?? null,
		numberOfDirectReports: u.numberOfDirectReports ?? undefined,
		numberOfTotalReports: u.numberOfTotalReports ?? undefined,
	};
}

export function mapKnowledgeDiscoveryReportingLinesToHierarchyData(
	data: luminaUserProfilePreviewRelay_reportingLines$data | null | undefined,
): HierarchyData | undefined {
	if (!data?.reportingLines) {
		return undefined;
	}
	const rl = data.reportingLines;
	if (rl.__typename !== 'KnowledgeDiscoveryReportingLines') {
		return undefined;
	}
	const user = mapKdReportingLinesUser(rl.user);
	if (!user) {
		return undefined;
	}
	const managers = (rl.managers ?? [])
		.map((m) => mapKdReportingLinesUser(m))
		.filter((m): m is HierarchyData['user'] => m != null);
	const reports =
		rl.reports
			?.map((r) => mapKdReportingLinesUser(r))
			.filter((r): r is HierarchyData['user'] => r != null) ?? [];
	return {
		managers: managers.length ? managers : undefined,
		user,
		reports: reports.length ? reports : undefined,
	};
}

export function mapRelaySmartsToCollaborators(
	data: luminaUserProfilePreviewRelay_smarts$data,
	accountId?: string,
): Collaborator[] {
	const recommended = data.recommendedUser ?? [];
	if (!recommended.length) {
		return [];
	}

	return recommended.reduce<Collaborator[]>((acc, node) => {
		const user = node?.user;
		if (!user) {
			return acc;
		}
		const id = user.accountId;
		if (!id || (accountId && id === accountId)) {
			return acc;
		}
		acc.push({
			accountId: id ?? null,
			name: user.name ?? null,
			avatarUrl: user.picture ?? null,
			email: user.email ?? null,
			jobTitle: user.extendedProfile?.jobTitle ?? null,
		});
		return acc;
	}, []);
}

export function UserProfilePreviewRelay({
	userFragment,
	workedOnFragment,
	smartsFragment,
	reportingLinesFragment,
	orgId,
	siteId,
	...props
}: UserProfilePreviewRelayProps): React.JSX.Element {
	const userProfileData = useFragment(
		graphql`
			fragment luminaUserProfilePreviewRelay_user on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				accountId: { type: "String!" }
				requestingUserId: { type: "String!" }
				tenantId: { type: "String!" }
			) {
				singleUser(accountId: $accountId, requestingUserId: $requestingUserId, tenantId: $tenantId)
					@optIn(to: "KnowledgeDiscovery Single User") {
					__typename
					... on KnowledgeDiscoverySingleUser {
						user {
							id
							name
							picture
							email
							jobTitle
							department
							location
							organization
							thirdPartyIntegrations {
								externalId
								integrationId
								product
							}
						}
					}
				}
			}
		`,
		userFragment ?? null,
	);

	const workedOnData = useFragment(
		graphql`
			fragment luminaUserProfilePreviewRelay_workedOn on ActivityConnection {
				edges {
					node {
						object {
							data {
								__typename
								... on ConfluencePage {
									links {
										base
										webUi
									}
								}
								... on ConfluenceBlogPost {
									links {
										base
										webUi
									}
								}
								... on ConfluenceWhiteboard {
									links {
										base
										webUi
									}
								}
								... on ConfluenceInlineComment {
									links {
										base
										webUi
									}
								}
								... on ConfluenceFooterComment {
									links {
										base
										webUi
									}
								}
								... on JiraIssue {
									webUrl
								}
								... on JiraPlatformComment {
									webUrl
									issue {
										webUrl
									}
								}
								... on JiraServiceManagementComment {
									webUrl
									issue {
										webUrl
									}
								}
								... on ExternalDocument {
									url
								}
								... on BitbucketPullRequest {
									href
								}
								... on LoomVideo {
									loomUrl: url
								}
							}
						}
					}
				}
			}
		`,
		workedOnFragment ?? null,
	);

	const smartsData = useFragment(
		graphql`
			fragment luminaUserProfilePreviewRelay_smarts on SmartsQueryApi
			@argumentDefinitions(recommendationsQuery: { type: "SmartsRecommendationsQuery!" }) {
				recommendedUser(recommendationsQuery: $recommendationsQuery) {
					user {
						accountId
						name
						picture
						... on AtlassianAccountUser {
							email
							extendedProfile {
								jobTitle
							}
						}
					}
				}
			}
		`,
		smartsFragment ?? null,
	);

	const reportingLinesData = useFragment(
		graphql`
			fragment luminaUserProfilePreviewRelay_reportingLines on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				aaid: { type: "String!" }
				orgId: { type: "String!" }
				siteId: { type: "String!" }
			) {
				reportingLines(aaid: $aaid, orgId: $orgId, siteId: $siteId)
					@optIn(to: "KnowledgeDiscovery Get reporting lines") {
					__typename
					... on KnowledgeDiscoveryReportingLines {
						user {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						managers {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						reports {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
					}
				}
			}
		`,
		reportingLinesFragment ?? null,
	);

	const relayHierarchyData = useMemo(
		() => mapKnowledgeDiscoveryReportingLinesToHierarchyData(reportingLinesData),
		[reportingLinesData],
	);

	const recentWorkData = useMemo(() => {
		if (!workedOnData) {
			return undefined;
		}
		const mapped = mapRelayWorkedOnConnectionToRecentWorkItems(workedOnData);
		return mapped.length ? mapped : undefined;
	}, [workedOnData]);

	const relayProfileData = useMemo(() => {
		const result = userProfileData?.singleUser;
		if (!result || result.__typename !== 'KnowledgeDiscoverySingleUser') {
			return undefined;
		}
		const user = result.user;
		if (!user?.id) {
			return undefined;
		}
		return {
			accountId: user.id,
			name: user.name ?? '',
			avatarUrl: user.picture ?? null,
			email: user.email ?? null,
			jobTitle: user.jobTitle ?? null,
			department: user.department ?? null,
			location: user.location ?? null,
			organization: user.organization ?? null,
			thirdPartyIntegrations:
				user.thirdPartyIntegrations?.map((integration) => ({
					externalId: integration.externalId,
					integrationId: integration.integrationId,
					product: integration.product,
				})) ?? null,
		};
	}, [userProfileData]);

	const relayCollaborators = useMemo(() => {
		if (!smartsData) {
			return undefined;
		}
		const mapped = mapRelaySmartsToCollaborators(smartsData, relayProfileData?.accountId);
		return mapped.length ? mapped : undefined;
	}, [smartsData, relayProfileData?.accountId]);

	return (
		<UserProfilePreview
			{...props}
			profileData={relayProfileData ?? props.profileData}
			hierarchyData={relayHierarchyData ?? props.hierarchyData}
			recentWorkData={recentWorkData}
			collaborators={relayCollaborators ?? props.collaborators}
			orgId={orgId}
			siteId={siteId}
		/>
	);
}
